import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import type { TagDetailOut } from '@/api/generated/schemas';
import { LabelsTags } from '@/components/common/LabelsField';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { StatCell } from '@/components/common/show/StatCell';
import { TAG_SHOW_TABS } from '../constants';
import type { TagShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<TagShowTab, string> = {
  general: 'base.sections.basic_info',
  publications: 'user_catalog.common.tabs.publications',
  collection: 'base.sections.collection_info',
  search: 'user_catalog.common.tabs.search',
};

interface TagShowHeaderProps {
  record: TagDetailOut;
  activeTab: TagShowTab;
  onTabChange: (key: string) => void;
}

export const TagShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: TagShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.cover_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Title>
            <LabelsTags labels={record.labels} />
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('base.fields.mod_status')}>
              <ModStatusTag value={record.mod_status} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={TAG_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
